import React from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { useNewTranslation } from 'hooks/index'
import i18n from 'config/i18n'
import * as yup from 'yup'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { SubmissionState, PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TaskState, EngagementId, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { ActionCell } from 'components/Shared/MuiTable/Cell/ActionCell'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import Inputs, { NumberInputGroup } from 'components/Inputs'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { PLATFORM_ENUM } from 'services/api/constants'
import { getEngagementHeaderTranslation } from 'components/utils/translationsMap'
import RectangleButton from 'particles/Button/RectangleButton'
import Text from 'particles/Text'
import { SubmissionFormContainer } from './styles'
import { GridColDefProps } from '../SimpleTable/constants'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    socialMediaInfo: {
        profiles: SocialMediaAccount[]
    }
    insights_screenshot_files: string[]
    view_count_screenshot_files: string[]
    follower_count: {
        value: string
        color?: string
    }
    engagement_impression_count?: {
        value: string
        color?: string
    }
    engagement_like_count?: {
        value: string
        color?: string
    }
    engagement_reach_count?: {
        value: string
        color?: string
    }
    engagement_sticker_tap_count?: {
        value: string
        color?: string
    }
    engagement_view_count?: {
        value: string
        color?: string
    }
    engagement_link_click_count?: {
        value: string
        color?: string
    }
    linkInBioUrl: string
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
    isMultiSelectMode: boolean
}

interface GetColumnsParams {
    showLinkInBio: boolean
    engagementTypes: EngagementId[]
    isMultiSelectMode: boolean
    canManageTag: boolean
    disableRequestEdit?: boolean
    disableApprove?: boolean
    isCampaignEnded?: boolean
}
export const getColumns = ({
    showLinkInBio,
    engagementTypes,
    isMultiSelectMode,
    canManageTag,
    disableRequestEdit,
    disableApprove,
    isCampaignEnded,
}: GetColumnsParams): GridColDefProps[] => {
    return [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
            subFields: [
                {
                    field: FIELDS_TYPE.IG_USERNAME,
                    headerName: i18n.t('submission.table.instagramUsernameLabel'),
                },
                {
                    field: FIELDS_TYPE.FB_USERNAME,
                    headerName: i18n.t('submission.table.facebookUsernameLabel'),
                },
                {
                    field: FIELDS_TYPE.TIKTOK_USERNAME,
                    headerName: i18n.t('submission.table.tiktokUsernameLabel'),
                },
                {
                    field: FIELDS_TYPE.TWITTER_USERNAME,
                    headerName: i18n.t('submission.table.twitterUsernameLabel'),
                },
            ],
        },
        {
            field: 'view_count_screenshot_files',
            headerName: i18n.t('submission.table.storyScreenshotLabel'),
            width: 175,
            renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'insights_screenshot_files',
            headerName: i18n.t('submission.table.engagementScreenshotLabel'),
            width: 220,
            renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'follower_count',
            headerName: i18n.t('submission.table.followerCountLabel'),
            width: 240,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: getInfoTooltipHeaderCell({
                width: '190px',
                title: i18n.t('submission.table.followerCountLabel'),
                description: i18n.t(
                    'submission.table.followerCountTooltip',
                    'The number Partiposters entered when submitting engagement result for the campaign',
                ),
            }),
        },
        ...(engagementTypes.includes(EngagementId.VIEW)
            ? [
                  {
                      field: 'engagement_view_count',
                      headerName: i18n.t('submission.table.viewsLabel'),
                      width: 105,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.LIKE)
            ? [
                  {
                      field: 'engagement_like_count',
                      headerName: i18n.t('submission.table.likesLabel'),
                      width: 105,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.STICKER_TAP)
            ? [
                  {
                      field: 'engagement_sticker_tap_count',
                      headerName: i18n.t('submission.table.stickerTapsLabel'),
                      width: 125,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.REACH)
            ? [
                  {
                      field: 'engagement_reach_count',
                      headerName: i18n.t('submission.table.reachLabel'),
                      width: 105,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.IMPRESSION)
            ? [
                  {
                      field: 'engagement_impression_count',
                      headerName: i18n.t('submission.table.impressionLabel'),
                      width: 125,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.LINK_CLICK)
            ? [
                  {
                      field: 'engagement_link_click_count',
                      headerName: i18n.t('submission.table.linkClicksLabel'),
                      width: 125,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        {
            field: 'linkInBioUrl',
            type: 'string',
            headerName: i18n.t('submission.table.linkInBioLabel'),
            width: 170,
            renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
            hide: !showLinkInBio,
            sortable: false,
            filterable: false,
        },
        ...(canManageTag
            ? [
                  {
                      field: 'partiposterTags',
                      headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                      width: 240,
                      minWidth: 200,
                      renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(!isMultiSelectMode && !isCampaignEnded
            ? [
                  {
                      field: 'actions',
                      headerName: i18n.t('submission.table.actionsLabel'),
                      width: 145,
                      renderCell: (props: GridRenderCellParams) => (
                          <ActionCell
                              {...props}
                              disableRequestEdit={disableRequestEdit}
                              disableApprove={disableApprove}
                          />
                      ),
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
    ]
}

export const getRows = (
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    task: TaskState,
    isMultiSelectMode: boolean,
): RowsProps[] => {
    return submissions.map(submission => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.IG,
        )[0]
        const fbProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )[0]
        const tiktokProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )[0]
        const twitterProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TWITTER,
        )[0]
        let linkInBioUrl
        if (task && task.data) {
            if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK) {
                linkInBioUrl = task.data['link_in_bio_url']
            } else if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK) {
                if (submission.submission_task != undefined) {
                    linkInBioUrl = submission.submission_task.data['link_in_bio_url']
                }
            }
        }
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: {
                profile,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile],
            },
            insights_screenshot_files: submission.data && submission.data.insights_screenshot_files,
            view_count_screenshot_files: submission.data && submission.data.view_count_screenshot_files,
            follower_count: { value: submission.data && submission.data.follower_count },
            engagement_impression_count: { value: submission.data && submission.data.engagement_impression_count },
            engagement_like_count: { value: submission.data && submission.data.engagement_like_count },
            engagement_reach_count: { value: submission.data && submission.data.engagement_reach_count },
            engagement_sticker_tap_count: { value: submission.data && submission.data.engagement_sticker_tap_count },
            engagement_view_count: { value: submission.data && submission.data.engagement_view_count },
            engagement_link_click_count: { value: submission.data && submission.data.engagement_link_click_count },
            linkInBioUrl: linkInBioUrl,
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
            isMultiSelectMode: isMultiSelectMode,
        }
    })
}

export interface DataValidationProps {
    [key: string]: yup.NumberSchema
}

export const ApproveSubmission: React.FC<any> = ({ handleApprove, handleClear, data, engagementTypes }) => {
    const [newT] = useNewTranslation()
    const dataValidatation: DataValidationProps = {}
    engagementTypes.map((engagementType: EngagementId) => {
        const key = 'engagement_' + engagementType + '_count'
        const engagementHeader = getEngagementHeaderTranslation[engagementType as EngagementId]()
        dataValidatation[key] = yup
            .number()
            .min(0, newT('errorMessage.validation.enterNonNegativeNumbers'))
            .required(newT('errorMessage.validation.enterMissingMetric', { engagementMetric: engagementHeader }))
    })
    const followerCountHeader = getEngagementHeaderTranslation['follower_count']()
    dataValidatation['follower_count'] = yup
        .number()
        .min(0, newT('errorMessage.validation.enterNonNegativeNumbers'))
        .required(newT('errorMessage.validation.enterMissingMetric', { engagementMetric: followerCountHeader }))

    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(newT('errorMessage.validation.enterComment', 'Please enter comment.')),
        data: yup.object(dataValidatation),
    })

    return (
        <Formik
            initialValues={{
                comment: data.comment,
                data: {
                    follower_count: data.data.follower_count,
                    engagement_impression_count: data.data.engagement_impression_count,
                    engagement_like_count: data.data.engagement_like_count,
                    engagement_reach_count: data.data.engagement_reach_count,
                    engagement_sticker_tap_count: data.data.engagement_sticker_tap_count,
                    engagement_view_count: data.data.engagement_view_count,
                    engagement_link_click_count: data.data.engagement_link_click_count,
                },
            }}
            enableReinitialize={true}
            validationSchema={validation}
            onSubmit={(values: { comment: string | null; data: any }, actions): void => {
                handleApprove(data.uuid, values.comment, values.data)
                handleClear()
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<any>) => {
                return (
                    <Form>
                        <SubmissionFormContainer>
                            <Text scale="bodyS500" textAlign="left">
                                {newT(
                                    'submission.review.editMetricsInstruction',
                                    'You can edit the numbers below if the numbers provided are incorrect',
                                )}
                            </Text>
                            <NumberInputGroup>
                                <Inputs.Number
                                    label={newT('submission.table.followerCountLabel')}
                                    name="data.follower_count"
                                    inputSize="s"
                                    required
                                    width="100%"
                                />
                                {engagementTypes.includes(EngagementId.VIEW) && (
                                    <Inputs.Number
                                        label={newT('engagementMetric.view', 'Number of Views')}
                                        name="data.engagement_view_count"
                                        inputSize="s"
                                        required
                                        width="100%"
                                    />
                                )}
                                {engagementTypes.includes(EngagementId.LIKE) && (
                                    <Inputs.Number
                                        label={newT('engagementMetric.like', 'Number of Likes')}
                                        name="data.engagement_like_count"
                                        inputSize="s"
                                        required
                                        width="100%"
                                    />
                                )}
                                {engagementTypes.includes(EngagementId.STICKER_TAP) && (
                                    <Inputs.Number
                                        label={newT('engagementMetric.stickerTap', 'Number of Sticker taps')}
                                        name="data.engagement_sticker_tap_count"
                                        inputSize="s"
                                        required
                                        width="100%"
                                    />
                                )}
                                {engagementTypes.includes(EngagementId.REACH) && (
                                    <Inputs.Number
                                        label={newT('engagementMetric.reach', 'Reach')}
                                        name="data.engagement_reach_count"
                                        inputSize="s"
                                        required
                                        width="100%"
                                    />
                                )}
                                {engagementTypes.includes(EngagementId.IMPRESSION) && (
                                    <Inputs.Number
                                        label={newT('engagementMetric.impression', 'Impression')}
                                        name="data.engagement_impression_count"
                                        inputSize="s"
                                        required
                                        width="100%"
                                    />
                                )}
                                {engagementTypes.includes(EngagementId.LINK_CLICK) && (
                                    <Inputs.Number
                                        label={newT('engagementMetric.linkClick')}
                                        name="data.engagement_link_click_count"
                                        inputSize="s"
                                        required
                                        width="100%"
                                    />
                                )}
                            </NumberInputGroup>
                            <div>
                                <Text scale="bodyS500" textAlign="left" style={{ marginBottom: 4 }}>
                                    {newT('submission.review.commentsForPartiposterHeader', 'Comments for Partiposter')}
                                </Text>
                                <Text scale="bodyS400" textAlign="left" style={{ marginBottom: 8 }}>
                                    {newT('submission.review.commentsForPartiposterSubtext')}
                                </Text>
                                <Inputs.TextArea
                                    name="comment"
                                    placeholder={newT('placeholderText.enterCommentForPartiposter')}
                                    minTextAreaHeight={64}
                                    maxTextAreaHeight={332}
                                />
                            </div>

                            <div className="btn-group">
                                <RectangleButton
                                    form="outlined"
                                    size="l"
                                    label={newT('button.cancel')}
                                    onClick={handleClear}
                                />
                                <RectangleButton form="filled" size="l" type="submit" label={newT('button.approve')} />
                            </div>
                        </SubmissionFormContainer>
                    </Form>
                )
            }}
        </Formik>
    )
}
